import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getStripeLanguage } from '../../../../i18n'
import { getPlansBaseValues } from '../../../../services/planService'
import { getAddonPackages } from '../../../../services/stripeService'
import { getActiveSubscription } from '../../../../services/subscriptionService'
import {
  UPSELL_CAMPAIGN_PACKAGE_CODE,
  UPSELL_DOWNLOAD_PACKAGE_CODE,
  UPSELL_FEATURED_PACKAGE_CODE,
  UPSELL_FEATURED_PLAN_TIER,
  buildFeaturedPackageOffer,
  buildFeaturedPlanOffer,
  buildRechargeFeaturedOffer,
  buildUpgradeFeaturedOffer,
  formatUpsellPrice,
  getPackagePrice,
  getCurrentPlanTier,
  getFeaturedUpgradePlan,
  getSelectablePlans,
  getPackageByCode,
  getPlanByTier,
  mapPackagesToOptions,
  mapPlansToOptions,
  mapUpgradePlansToOptions,
} from './upsellCatalog'

export function useUpsellCatalog({ enabled = true } = {}) {
  const { i18n } = useTranslation()
  const language = i18n.language || getStripeLanguage()
  const [dbPlans, setDbPlans] = useState([])
  const [dbPackages, setDbPackages] = useState([])
  const [currentPlanTier, setCurrentPlanTier] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false

    const fetchSubscription = async () => {
      try {
        const response = await getActiveSubscription()
        if (cancelled) return

        setCurrentPlanTier(getCurrentPlanTier(response?.data))
      } catch (error) {
        if (!cancelled) {
          setCurrentPlanTier(0)
        }
      }
    }

    fetchSubscription()

    return () => {
      cancelled = true
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false

    const fetchCatalog = async () => {
      setIsLoading(true)

      try {
        const [plansResponse, packagesResponse] = await Promise.all([
          getPlansBaseValues(language),
          getAddonPackages(language),
        ])

        if (!cancelled) {
          if (plansResponse?.data) {
            setDbPlans(plansResponse.data)
          }

          if (packagesResponse?.data) {
            setDbPackages(packagesResponse.data)
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchCatalog()

    return () => {
      cancelled = true
    }
  }, [enabled, language])

  const featuredPlan = useMemo(
    () => getPlanByTier(dbPlans, UPSELL_FEATURED_PLAN_TIER),
    [dbPlans],
  )

  const featuredUpgradePlan = useMemo(
    () => getFeaturedUpgradePlan(dbPlans, currentPlanTier),
    [dbPlans, currentPlanTier],
  )

  const hasUpgradePlans = Boolean(featuredUpgradePlan)

  const hasSelectablePlans = useMemo(() => {
    if (!dbPlans.length) {
      return true
    }

    return getSelectablePlans(dbPlans, currentPlanTier).length > 0
  }, [dbPlans, currentPlanTier])

  const featuredPackage = useMemo(
    () => getPackageByCode(UPSELL_FEATURED_PACKAGE_CODE, dbPackages),
    [dbPackages],
  )

  const campaignPackage = useMemo(
    () => getPackageByCode(UPSELL_CAMPAIGN_PACKAGE_CODE, dbPackages),
    [dbPackages],
  )

  const downloadPackage = useMemo(
    () => getPackageByCode(UPSELL_DOWNLOAD_PACKAGE_CODE, dbPackages),
    [dbPackages],
  )

  const getPlanOptions = useCallback(
    (excludeTiers = []) =>
      mapPlansToOptions(dbPlans, {
        language,
        excludeTiers,
      }),
    [dbPlans, language],
  )

  const getPackageOptions = useCallback(
    (excludeCodes = []) =>
      mapPackagesToOptions({
        language,
        excludeCodes,
        packages: dbPackages,
      }),
    [dbPackages, language],
  )

  const getUpgradePlanOptions = useCallback(() => {
    const featuredTier = featuredUpgradePlan?.tier

    return mapUpgradePlansToOptions(dbPlans, currentPlanTier, {
      language,
      excludeTiers: featuredTier ? [featuredTier] : [],
    })
  }, [currentPlanTier, dbPlans, featuredUpgradePlan, language])

  return {
    language,
    dbPlans,
    dbPackages,
    currentPlanTier,
    hasUpgradePlans,
    hasSelectablePlans,
    isLoading,
    featuredPlan,
    featuredUpgradePlan,
    featuredPackage,
    campaignPackage,
    downloadPackage,
    formatPrice: (price, currency) =>
      formatUpsellPrice(price, language, currency),
    formatPackagePrice: (pkg) => getPackagePrice(pkg, language),
    getPlanOptions,
    getUpgradePlanOptions,
    getPackageOptions,
    buildFeaturedPlanOffer: (plan = featuredUpgradePlan ?? featuredPlan) =>
      buildFeaturedPlanOffer(plan, language),
    buildFeaturedPackageOffer: (pkg, options) =>
      buildFeaturedPackageOffer(pkg, language, options),
    buildRechargeFeaturedOffer: (pkg = featuredPackage) =>
      buildRechargeFeaturedOffer(pkg, language),
    buildUpgradeFeaturedOffer: (plan = featuredUpgradePlan ?? featuredPlan) =>
      buildUpgradeFeaturedOffer(plan, language),
  }
}

export function useUpgradePlansTab(
  catalog,
  activeTab,
  setActiveTab,
  tabs,
) {
  const availableTabs = useMemo(
    () =>
      catalog.hasSelectablePlans
        ? tabs
        : tabs.filter((tab) => tab.id !== 'plans'),
    [catalog.hasSelectablePlans, tabs],
  )

  useEffect(() => {
    if (!catalog.hasSelectablePlans && activeTab === 'plans') {
      setActiveTab('packages')
    }
  }, [activeTab, catalog.hasSelectablePlans, setActiveTab])

  return availableTabs
}
